import React from 'react';
import { RocketLaunchIcon, ClockIcon, BuildingOfficeIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { usePortfolioData } from '../context/PortfolioDataContext';
import SectionWithNeuralBg from './SectionWithNeuralBg';
import TypewriterText from './TypewriterText';
import AnimatedSection from './AnimatedSection';

const TYPEWRITER_PHRASES = [
  'AI-powered applications',
  'secure & scalable systems',
  'intelligent chat experiences',
  'modern full-stack products',
];

const STATS = [
  { icon: RocketLaunchIcon, value: '20+', label: 'Projects Built' },
  { icon: ClockIcon, value: '6+', label: 'Years Experience' },
  { icon: BuildingOfficeIcon, value: '4', label: 'Companies' },
];

export default function Hero() {
  const { data } = usePortfolioData();
  const personalInfo = data.personalInfo;

  return (
    <SectionWithNeuralBg className="py-20 sm:py-28 lg:py-32 min-h-[85vh] flex items-center">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-96 h-96 bg-primary-500/10 rounded-full blur-3xl -top-48 -left-48 animate-float" />
        <div className="absolute w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl -bottom-48 -right-48 animate-float" style={{ animationDelay: '2s' }} />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <div className="max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <div className="mb-8 flex justify-center">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-400 via-violet-500 to-cyan-400 rounded-full blur-lg opacity-75 group-hover:opacity-100 transition duration-300 animate-glow" />
                <img
                  src={personalInfo.profileImage}
                  alt={personalInfo.name}
                  className="relative w-32 h-32 rounded-full border-4 border-gray-800 object-cover transform group-hover:scale-105 transition duration-300"
                />
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={100}>
            <div className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-900/30 text-primary-400 text-sm font-medium border border-primary-800/50">
                <SparklesIcon className="w-4 h-4" />
                {personalInfo.tagline}
              </span>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={200}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-white">I build </span>
              <span className="text-gradient block sm:inline min-h-[1.2em]">
                <TypewriterText phrases={TYPEWRITER_PHRASES} />
              </span>
            </h1>
          </AnimatedSection>

          <AnimatedSection delay={300}>
            <p className="text-lg sm:text-xl text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
              {personalInfo.description}
            </p>
          </AnimatedSection>

          <AnimatedSection delay={400}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a href="#projects" className="btn-primary w-full sm:w-auto">
                View My Work
              </a>
              <a href="#contact" className="btn-secondary w-full sm:w-auto">
                Get in Touch
              </a>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={500} className="mt-16">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
              {STATS.map((stat, i) => {
                const Icon = stat.icon;
                return (
                  <div
                    key={stat.label}
                    className="glass-card p-6 text-center group hover:border-primary-600/50 transition-all duration-300 transform hover:-translate-y-1"
                    style={{ transitionDelay: `${i * 80}ms` }}
                  >
                    <div className="flex justify-center mb-3">
                      <div className="w-12 h-12 bg-primary-900/30 rounded-full flex items-center justify-center group-hover:bg-primary-900/50 transition-colors">
                        <Icon className="w-6 h-6 text-primary-400" />
                      </div>
                    </div>
                    <div className="text-4xl font-bold text-white mb-1 group-hover:text-gradient transition-all">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-400">{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </AnimatedSection>
        </div>
      </div>
    </SectionWithNeuralBg>
  );
}
